import { useState } from "react";
import {
    isGettingCode,
    isSuccess,
    EPortInStep,
} from "@features/PortIn/components/ValidatePhoneNumber/enum/EPortInStep";
import { PhoneNumberView } from "./PhoneNumberView";
import { useTranslation } from "react-i18next";
import { UseFormMethods, useWatch } from "react-hook-form";
import { IMsisdn, INetwork, IPortinForm } from "@features/PortIn/interface/IPortinForm";
import { usePhoneNumber } from "@features/PortIn/components/ValidatePhoneNumber/PhoneNumber/hook/usePhoneNumber";

interface IParams {
    phoneNumberSelected: string;
    formMethods: UseFormMethods<IPortinForm>;
    hasTooManyAttempt: boolean;
    portInStep: EPortInStep;
    previousPhoneNumber?: string;
    variantStyleButton: "outlined" | "contained";
    setIsFinished(newValue: boolean): void;
    setOtpId(otpId: string): void;
    updatePortInStep(nextStep: EPortInStep): void;
    setCurrentProvider(currentProvider: INetwork): void;
    setHasTooManyAttempt(hasTooManyAttempt: boolean): void;
    setPhoneNumberSelected(phoneNumber: string): void;
}

export const PhoneNumber = (props: IParams) => {
    const {
        variantStyleButton,
        updatePortInStep,
        setIsFinished,
        portInStep,
        previousPhoneNumber,
        setOtpId,
        formMethods,
        setCurrentProvider,
        hasTooManyAttempt,
        setHasTooManyAttempt,
        setPhoneNumberSelected,
        phoneNumberSelected,
    } = props;

    const { t } = useTranslation(["common", "portin"]);

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const { createOtpCode, validatePhoneNumber } = usePhoneNumber(setErrorMessage);

    const changeValue = (newPhoneNumber: string) => {
        const cleanValue = newPhoneNumber.replace(/\s/g, "");
        setHasTooManyAttempt(false);
        formMethods.setValue("phoneNumber", cleanValue);
    };

    const phoneNumberValue = useWatch<string>({
        name: "phoneNumber",
        control: formMethods.control,
        defaultValue: "",
    });

    const getCode = async () => {
        setErrorMessage(undefined);
        if (await formMethods.trigger("phoneNumber")) {
            if (previousPhoneNumber && previousPhoneNumber === phoneNumberValue) {
                updatePortInStep(EPortInStep.successCode);
                setIsFinished(true);
                setPhoneNumberSelected(phoneNumberValue);
            } else {
                setIsLoading(true);

                validatePhoneNumber(phoneNumberValue)
                    .then((result: IMsisdn) => {
                        if (result) {
                            if (result.isPortInProgressAlready) {
                                setErrorMessage(t("portin:portInInProgress"));
                                setIsLoading(false);
                            } else {
                                setCurrentProvider(result.network);
                                getOtpIdCode();
                            }
                        } else {
                            getOtpIdCode();
                        }
                    })
                    .catch(() => {
                        getOtpIdCode();
                    });
            }
        }
    };

    const getOtpIdCode = () => {
        createOtpCode(phoneNumberValue)
            .then((response) => {
                if (response) {
                    setOtpId(response.otpUuid);
                    updatePortInStep(EPortInStep.verifyCode);
                    setPhoneNumberSelected(phoneNumberValue);
                }
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                setErrorMessage(t("common:genericError"));
            });
    };

    return (
        <PhoneNumberView
            changeValue={changeValue}
            errorMessage={errorMessage}
            formMethods={formMethods}
            getCode={getCode}
            hasTooManyAttempt={hasTooManyAttempt}
            isGettingCode={isGettingCode(portInStep)}
            isLoading={isLoading}
            isSuccess={isSuccess(portInStep)}
            phoneNumberSelected={phoneNumberSelected}
            previousPhoneNumber={previousPhoneNumber}
            variantStyleButton={variantStyleButton}
        />
    );
};
